import React from "react";
import { Button, Icon, Modal } from "@accessdev/arc-ui-react";
import RedeemError from "../Redeem/RedeemError";
import OfferTerms from "../OfferTerms";
import useAuthHooks from "@Src/components/Auth/useAuthHooks";
import { connect } from "react-redux";
import { selectors } from "@ReduxStore/reducers";
import {
  DealsContext,
  getRedemptionTypes,
  REDEMPTION_TYPES,
  DEAL_STEP,
  FLYOUT_SCREENS,
} from "@Src/routes/deals/Deals.context";
import classNames from "classnames";
import "./OfferDetail.scss";
import { Trans } from "@lingui/react/macro";
import { t } from "@lingui/core/macro";
import { i18n } from "@lingui/core";
import { translate } from "@Src/components/Translation";
import { matchOfferToAttraction } from "@Src/utils/themePark.utils";
import { ROUTES } from "@Src/constants/Routes";

const { hasAuthenticatedUser, getProgramKey, getOfferMappings } = selectors;

function AuthButton(props) {
  const { SignInButton } = useAuthHooks();
  return <SignInButton {...props} />;
}

class OfferDetail extends React.Component {
  constructor(props) {
    super(props);
    this.offerTitleTextRef = React.createRef();
    this.menuOpenTrigger = React.createRef(null);
    this.menuCloseTrigger = React.createRef(null);
    this.offerTitleTextMaxHeight = 80;
    this.state = {
      RedeemOpen: false,
      isCouponModalOpen: false,
      RedeemType: "",
      RedeemLink: "",
      redeemError: false,
      offerTitleText: {
        text: "",
        needsTruncating: false,
        isTruncated: false,
      },
    };
  }

  componentDidMount() {
    // First translate the text - than handle the display for potential overflow
    translate(this.props.offer.offer_title, i18n._locale, this.handleOfferTitleTextDisplay);
  }

  handleOfferTitleTextDisplay = (text) => {
    this.setState(
      {
        offerTitleText: {
          ...this.state.offerTitleText,
          text,
        },
      },
      () => {
        // If title height is greater than 60px than set up the text to be truncated
        const titleRects = this.offerTitleTextRef?.current?.getBoundingClientRect();

        if (titleRects?.height > this.offerTitleTextMaxHeight) {
          this.setState({
            offerTitleText: {
              text,
              needsTruncating: true,
              isTruncated: true,
            },
          });
        }
      },
    );
  };

  openCouponModal = (id, link) => {
    this.setState({
      isCouponModalOpen: true,
      RedeemType: id,
      RedeemLink: link,
    });
  };

  closeCouponModal = () => {
    this.setState({ isCouponModalOpen: false });
  };

  handleUseCoupon = () => {
    if (this.isMerchantOffer) {
      this.props.navigate(ROUTES.RETAILER_PATH, {
        state: this.isMerchantOffer,
      });
    } else if (this.shouldShowRedeemMenu) {
      this.toggleRedeem();
    } else {
      this.handleRedeemClick(this.types[0]);
    }
  };

  toggleRedeem = () => {
    this.setState(
      (prevState) => ({
        RedeemOpen: !prevState.RedeemOpen,
      }),
      () => {
        setTimeout(() => {
          if (this.state.RedeemOpen) {
            this.menuCloseTrigger?.current?.focus();
          } else {
            this.menuOpenTrigger?.current?.focus();
          }
        }, 200);
      },
    );
  };

  handleRedeemClick = (type) => {
    const { offer } = this.props;
    this.selectAnOffer(offer);

    setTimeout(() => {
      let flyoutScreenType = null;
      switch (type) {
        case REDEMPTION_TYPES.ONLINE:
          flyoutScreenType = FLYOUT_SCREENS.SHOW_LINK;
          break;
        case REDEMPTION_TYPES.CALL:
          flyoutScreenType = FLYOUT_SCREENS.SHOW_PHONE;
          break;
        case REDEMPTION_TYPES.SHOW:
          flyoutScreenType = FLYOUT_SCREENS.SHOW_COUPON;
          break;
        case REDEMPTION_TYPES.PRINT:
          flyoutScreenType = FLYOUT_SCREENS.SHOW_PRINT_PREVIEW;
          break;
        default:
          break;
      }
      if (flyoutScreenType !== null)
        this.context.setNextStepAndScreen(DEAL_STEP.THIRD, flyoutScreenType);
    }, 200);
  };

  handleExpandTruncateClick = () => {
    this.setState((prevState) => ({
      offerTitleText: {
        ...prevState.offerTitleText,
        isTruncated: !prevState.offerTitleText.isTruncated,
      },
    }));
  };

  setRedeemError = () => {
    this.setState({
      redeemError: true,
    });
  };

  closeErrorModal = () => {
    this.setState({
      redeemError: false,
    });
  };

  selectAnOffer = (offer) => this.context.selectAnOffer(offer, true);

  getUseButtonLabel = () => {
    if (this.isMerchantOffer) {
      return t`Purchase`;
    }
    return t`Use Coupon`;
  };

  render() {
    const { offer, offerMappings } = this.props;
    const { offerTitleText, RedeemOpen } = this.state;
    this.types = getRedemptionTypes(offer);
    this.isLimitedUse = offer.uses_per_period !== "unlimited";
    this.canRedeem = !this.isLimitedUse || offer.number_of_uses_remaining !== 0;
    this.isMerchantOffer = matchOfferToAttraction(offer.offer_group_key, offerMappings);
    this.shouldShowRedeemMenu =
      !this.isMerchantOffer && (this.types.length > 1 || this.isLimitedUse);
    const menuClosedTabIndex = RedeemOpen ? "-1" : "";
    const menuOpenedTabIndex = RedeemOpen ? "" : "-1";

    return (
      <div className="offer-detail">
        <div className={`offer-detail__block ${RedeemOpen ? "open" : ""}`}>
          <div className="offer-detail__title t-title-med">
            <div ref={this.offerTitleTextRef} className="offer-detail__title-text">
              <span
                className={classNames({
                  truncated: offerTitleText.isTruncated,
                })}
              >
                {offerTitleText.text}
              </span>
              {offerTitleText.needsTruncating && (
                <div className="offer-detail__title-expandTruncate">
                  <button
                    onClick={this.handleExpandTruncateClick}
                    className="t-label-small"
                    type="button"
                    aria-expanded={!offerTitleText.isTruncated}
                  >
                    {offerTitleText.isTruncated ? t`More` : t`Less`}
                  </button>
                </div>
              )}
            </div>
            <OfferTerms tabIndex={menuClosedTabIndex} offer={offer} />
          </div>

          <div className="offer-detail__button-container">
            {this.props.userIsAuthenticated ? (
              <Button
                ref={this.menuOpenTrigger}
                additionalProps={{
                  id: "redeem-offer-" + offer.offer_key,
                  tabIndex: menuClosedTabIndex,
                }}
                size="large"
                onClick={this.handleUseCoupon}
              >
                <div className="offer-detail__use-text">{this.getUseButtonLabel()}</div>
              </Button>
            ) : (
              <AuthButton
                additionalProps={{
                  tabIndex: menuClosedTabIndex,
                }}
                size="large"
                data-testid="auth-button"
              />
            )}
          </div>
          {this.state.redeemError && (
            <Modal onClose={this.closeErrorModal}>
              <RedeemError btnText="Ok" btnCallback={this.closeErrorModal} />
            </Modal>
          )}
          {this.shouldShowRedeemMenu && (
            <button
              ref={this.menuCloseTrigger}
              className={`offer-detail__hide-button ${RedeemOpen ? "open" : ""}`}
              onClick={this.toggleRedeem}
              tabIndex={menuOpenedTabIndex}
              aria-label={t`Close redeem types menu`}
            >
              <Icon icon="arrow_right" />
            </button>
          )}
        </div>
        {this.shouldShowRedeemMenu && (
          <div className="offer-detail__offer-options">
            {this.isLimitedUse && (
              <div className="offer-detail__limit-prompt">
                <div className="offer-detail__limit-prompt-overlay t-label-small">
                  {this.canRedeem ? (
                    <Trans>Clicking button uses 1 of the {offer.uses_per_period} limit.</Trans>
                  ) : (
                    <Trans>
                      Sorry, you have used your {offer.uses_per_period} limit for this deal.
                    </Trans>
                  )}
                </div>
              </div>
            )}
            <span className="offer-detail__redeem-prompt">
              <Trans>How do you want to use this coupon?</Trans>
            </span>
            {this.types.includes(REDEMPTION_TYPES.SHOW) && (
              <Button
                className="offer-detail__redeem-btn"
                onClick={() => this.handleRedeemClick(REDEMPTION_TYPES.SHOW)}
                size="medium"
                isDisabled={!this.canRedeem}
                additionalProps={{
                  tabIndex: menuOpenedTabIndex,
                }}
              >
                <Trans>Show</Trans>
              </Button>
            )}
            {this.types.includes(REDEMPTION_TYPES.PRINT) && (
              <Button
                className="offer-detail__redeem-btn"
                onClick={() => this.handleRedeemClick(REDEMPTION_TYPES.PRINT)}
                size="medium"
                isDisabled={!this.canRedeem}
                additionalProps={{
                  tabIndex: menuOpenedTabIndex,
                }}
              >
                <Trans>Print</Trans>
              </Button>
            )}
            {this.types.includes(REDEMPTION_TYPES.ONLINE) && (
              <Button
                className="offer-detail__redeem-btn"
                onClick={() => this.handleRedeemClick(REDEMPTION_TYPES.ONLINE)}
                size="medium"
                isDisabled={!this.canRedeem}
                additionalProps={{
                  tabIndex: menuOpenedTabIndex,
                }}
              >
                <Trans>Online</Trans>
              </Button>
            )}
            {this.types.includes(REDEMPTION_TYPES.CALL) && (
              <Button
                className="offer-detail__redeem-btn"
                onClick={() => this.handleRedeemClick(REDEMPTION_TYPES.CALL)}
                size="medium"
                isDisabled={!this.canRedeem}
                additionalProps={{
                  tabIndex: menuOpenedTabIndex,
                }}
              >
                <Trans>Call</Trans>
              </Button>
            )}
          </div>
        )}
      </div>
    );
  }
}

OfferDetail.contextType = DealsContext;

const mapStateToProps = (state) => ({
  userIsAuthenticated: hasAuthenticatedUser(state),
  programKey: getProgramKey(state),
  offerMappings: getOfferMappings(state),
});
export default connect(mapStateToProps, {})(OfferDetail);
